import React from "react"
import { Link } from "react-router-dom"
import { FieldArray, useFormikContext, Field } from "formik"

import CatchCard from "~/library/CatchCard"

import * as Yup from "yup"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import FormikSelect from "~/library/FormikSelect"
import CurrencyInput from "~/library/CurrencyInput"
import useFetchResource from "~/hooks/useFetchResource"
import Category from "~/types/Category"
import LineItem from "~/types/LineItem"
import { apiCategories } from "~/routes"

const lineItemSchema = Yup.object({
  name: Yup.string().required("Line item name is required"),
  amountCents: Yup.number().min(0, "Amount must be positive").required("Amount is required"),
  categoryId: Yup.string().nullable(),
  minOrderQty: Yup.number()
    .min(1, "Minimum order quantity must be at least 1")
    .required("Minimum order quantity is required"),
  active: Yup.boolean(),
})

interface LineItemFormProps {
  venueSlug: string
  onSubmit: (values: any) => void
  initialValues?: LineItem
  isEditing?: boolean
}

const LineItemForm: React.FC<LineItemFormProps> = ({
  venueSlug,
  onSubmit,
  initialValues,
  isEditing = false,
}) => {
  const { data: categories, isLoading } = useFetchResource<
    { categories: Category[] },
    "categories"
  >(apiCategories.index.path({ venueSlug }), "categories", {
    "q[active_eq]": "true",
  })

  const defaultValues = {
    name: "",
    amountCents: 0,
    categoryId: "",
    minOrderQty: 1,
    active: true,
    modifierGroups: [],
  }

  const categoryOptions = Array.isArray(categories)
    ? [
        ...categories.map((cat) => ({
          value: cat.uuid,
          label: cat.name,
        })),
      ]
    : []

  const handleFormSubmit = (values: any) => {
    const formData = {
      name: values.name,
      amountCents: values.amountCents,
      categoryId: values.categoryId,
      minOrderQty: values.minOrderQty,
      active: values.active,
      modifierGroupsAttributes:
        values.modifierGroups?.map((group: any) => ({
          ...group,
          modifiersAttributes: group.modifiers || [],
          modifiers: undefined,
        })) || [],
    }
    onSubmit(formData)
  }

  return (
    <FormikWrapper
      onSubmit={handleFormSubmit}
      validationSchema={lineItemSchema}
      initialValues={isEditing ? initialValues : defaultValues}
    >
      <CatchCard>
        <FormikInput
          name="name"
          label="Line Item Name"
          placeholder="Hot Dog"
          required
          helpText=""
        />

        <FormikSelect
          name="categoryId"
          label="Category"
          options={categoryOptions}
          helpText=""
          disabled={isLoading}
          placeholder={isLoading ? "Loading categories..." : "Select category"}
        />

        <CurrencyInput name="amountCents" label="Amount" placeholder="25.00" currency="USD" />

        <FormikInput
          name="minOrderQty"
          label="Minimum Order Qty"
          type="number"
          placeholder="1"
          required
          helpText="Minimum quantity that can be ordered"
        />

        <div className="mb-3">
          <div className="form-check">
            <Field className="form-check-input" type="checkbox" name="active" id="active" />
            <label className="form-check-label" htmlFor="active">
              Active
            </label>
          </div>
          <div className="form-text">Uncheck to hide this item from public access</div>
        </div>
      </CatchCard>

      <ModifierGroupsCard />

      <div className="d-flex">
        <div className="ms-auto">
          <Link className="btn btn-secondary me-2" to="/line-items">
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary">
            {isEditing ? "Update Line Item" : "Create Line Item"}
          </button>
        </div>
      </div>
    </FormikWrapper>
  )
}

const ModifierGroupsCard: React.FC = () => {
  const { values } = useFormikContext<any>()

  const defaultModifierGroupValues = {
    name: "",
    required: false,
    minAllowed: null,
    maxAllowed: null,
    modifiers: [{ name: "", priceCents: 0 }],
  }

  return (
    <CatchCard title="Product Modifiers">
      <FieldArray name="modifierGroups">
        {(arrayHelpers) => (
          <div>
            {values.modifierGroups && values.modifierGroups.length > 0
              ? values.modifierGroups
                  .map((group: any, groupIndex: number) => (
                    <ModifierGroupSection
                      key={groupIndex}
                      groupIndex={groupIndex}
                      arrayHelpers={arrayHelpers}
                    />
                  ))
                  .filter(
                    (_: any, groupIndex: number) => !values.modifierGroups[groupIndex]._destroy
                  )
              : null}

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => arrayHelpers.push(defaultModifierGroupValues)}
            >
              Add new Modifier Group
            </button>
          </div>
        )}
      </FieldArray>
    </CatchCard>
  )
}

const ModifierGroupSection: React.FC<{
  groupIndex: number
  arrayHelpers: any
}> = ({ groupIndex, arrayHelpers }) => {
  const { values } = useFormikContext<any>()
  const group = values.modifierGroups[groupIndex]

  const defaultModifierValues = {
    name: "",
    priceCents: 0,
  }

  return (
    <div className="modifier-group modifier-group--theme-sensitivity mb-4 p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Modifier Group</h5>
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          onClick={() => {
            if (group.id) {
              arrayHelpers.replace(groupIndex, { ...group, _destroy: true })
            } else {
              arrayHelpers.remove(groupIndex)
            }
          }}
        >
          Remove Group
        </button>
      </div>

      <div className="row mb-3">
        <div className="col-6">
          <FormikInput
            name={`modifierGroups[${groupIndex}].name`}
            label="Group Name"
            placeholder="Sides (Select 1)"
            helpText=""
            style={{ height: "38px" }}
          />
        </div>
        <div className="col-6">
          <div className="form-check mt-4">
            <Field
              className="form-check-input"
              type="checkbox"
              name={`modifierGroups[${groupIndex}].required`}
              id={`required-${groupIndex}`}
            />
            <label className="form-check-label" htmlFor={`required-${groupIndex}`}>
              Selection is required
            </label>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-6">
          <FormikInput
            name={`modifierGroups[${groupIndex}].minAllowed`}
            label="Minimum Allowed"
            type="number"
            placeholder="Not set"
            helpText="Minimum number of modifiers that must be selected"
            style={{ height: "38px" }}
          />
        </div>
        <div className="col-6">
          <FormikInput
            name={`modifierGroups[${groupIndex}].maxAllowed`}
            label="Maximum Allowed"
            type="number"
            placeholder="Not set"
            helpText="Maximum number of modifiers that can be selected"
            style={{ height: "38px" }}
          />
        </div>
      </div>

      <div className="modifiers-section">
        <FieldArray name={`modifierGroups[${groupIndex}].modifiers`}>
          {(modifierArrayHelpers) => (
            <div>
              {group.modifiers && group.modifiers.length > 0
                ? group.modifiers.map((modifier: any, modifierIndex: number) =>
                    !modifier._destroy ? (
                      <div key={modifierIndex} className="row mb-2 align-items-end">
                        <div className="col-6">
                          <FormikInput
                            containerClassName="mb-0"
                            name={`modifierGroups[${groupIndex}].modifiers[${modifierIndex}].name`}
                            label={modifierIndex === 0 ? "Modifier name" : ""}
                            placeholder="French Fries"
                            helpText=""
                            style={{ height: "38px" }}
                          />
                        </div>
                        <div className="col-4">
                          <CurrencyInput
                            inputProps={{ style: { height: "38px" } }}
                            containerClassName="mb-0"
                            name={`modifierGroups[${groupIndex}].modifiers[${modifierIndex}].priceCents`}
                            label={modifierIndex === 0 ? "Modifier Price" : ""}
                            currency="USD"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="col-2">
                          <button
                            type="button"
                            style={{ height: "38px", width: "38px" }}
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => {
                              if (modifier.id) {
                                modifierArrayHelpers.replace(modifierIndex, {
                                  ...modifier,
                                  _destroy: true,
                                })
                              } else {
                                modifierArrayHelpers.remove(modifierIndex)
                              }
                            }}
                          >
                            x
                          </button>
                        </div>
                      </div>
                    ) : null
                  )
                : null}

              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => modifierArrayHelpers.push(defaultModifierValues)}
              >
                Add new modifier
              </button>
            </div>
          )}
        </FieldArray>
      </div>
    </div>
  )
}

export default LineItemForm
